// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'

/** D-UI-3 — form fields of the kit (pages import the kit, never @mui directly). */
export function AppTextField(props: TextFieldProps) {
  return <TextField {...props} />
}

/** Keeps only digits and a single dot while typing; the caller parses on save. */
export function sanitizeMoney(v: string): string {
  const digits = v.replace(/[^0-9.]/g, '')
  const dot = digits.indexOf('.')
  return dot < 0 ? digits : digits.slice(0, dot + 1) + digits.slice(dot + 1).replace(/\./g, '')
}

/** Money in dollars, typed as text. */
export function MoneyField({ value, onChange, ...rest }: Omit<TextFieldProps, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void }) {
  return (
    <TextField {...rest} value={value} inputMode="decimal" onChange={e => onChange(sanitizeMoney(e.target.value))}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
  )
}

export interface FieldOption { value: string; label: string }

/** A short fixed list (no typeahead needed); long lists cap the open menu's height. Data-bound lists use SearchableSelect. */
export function SelectField({ options, value, onChange, maxMenuHeight = 320, ...rest }: Omit<TextFieldProps, 'onChange' | 'value' | 'select'> & {
  options: FieldOption[]; value: string; onChange: (v: string) => void; maxMenuHeight?: number
}) {
  return (
    <TextField {...rest} select value={value} onChange={e => onChange(e.target.value)}
      SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: maxMenuHeight } } } }}
      InputLabelProps={{ shrink: true }}>
      {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  )
}

/** Free text with suggestions (e.g. a category you can also invent). */
export function SuggestField({ value, onChange, suggestions, label, required, maxLength }: {
  value: string; onChange: (v: string) => void; suggestions: string[]; label: string; required?: boolean; maxLength?: number
}) {
  return (
    <Autocomplete freeSolo options={suggestions} value={value} onInputChange={(_, v) => onChange(v)}
      renderInput={p => <TextField {...p} label={label} required={required} inputProps={{ ...p.inputProps, maxLength }} />} />
  )
}

export interface EmailSuggestion { email: string; hint?: string }

/**
 * An email typed freely, with known addresses offered as suggestions (ASP 2026-10-14 #6: a picker that
 * only accepted listed addresses dropped what was typed, and focus moved on). What is in the box is the
 * value, typed or picked, so nothing reverts when the field loses focus.
 */
export function EmailSuggestField({ value, onChange, suggestions, ariaLabel, placeholder, error, helperText, autoFocus }: {
  value: string; onChange: (v: string) => void; suggestions: EmailSuggestion[]; ariaLabel: string
  placeholder?: string; error?: boolean; helperText?: string; autoFocus?: boolean
}) {
  return (
    <Autocomplete freeSolo disableClearable size="small" options={suggestions}
      getOptionLabel={o => typeof o === 'string' ? o : o.email}
      filterOptions={(opts, s) => {
        const q = s.inputValue.trim().toLowerCase()
        // A box holding a known address in full offers every address again, not just itself.
        if (!q || opts.some(o => o.email.toLowerCase() === q)) return opts
        return opts.filter(o => o.email.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q))
      }}
      inputValue={value} onInputChange={(_, v, reason) => { if (reason !== 'reset' || v) onChange(v) }}
      onChange={(_, v) => onChange(typeof v === 'string' ? v : v?.email ?? '')}
      renderOption={(props, o) => (
        <li {...props} key={o.email}>
          <span>{o.email}</span>
          {o.hint && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--color-text-mute)' }}>{o.hint}</span>}
        </li>
      )}
      renderInput={p => (
        <TextField {...p} type="email" placeholder={placeholder} autoFocus={autoFocus} error={error} helperText={helperText}
          inputProps={{ ...p.inputProps, 'aria-label': ariaLabel, autoComplete: 'off' }} />
      )} />
  )
}

/** One-of-a-few choice shown as joined buttons. */
export function ChoiceToggle<T extends string>({ value, onChange, options, ariaLabel }: {
  value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; ariaLabel: string
}) {
  return (
    <ToggleButtonGroup exclusive size="small" value={value} aria-label={ariaLabel}
      onChange={(_, v: T | null) => { if (v) onChange(v) }}>
      {options.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  )
}

/**
 * One-of-a-few choice as a labelled radio group, for a question that may stay UNANSWERED: `value`
 * '' means nothing picked (ChoiceToggle always holds a value). D-CRX-86.
 */
export function RadioChoice<T extends string>({ value, onChange, options, label, disabled }: {
  value: T | ''; onChange: (v: T) => void; options: { value: T; label: string }[]; label: string; disabled?: boolean
}) {
  return (
    <FormControl component="fieldset" disabled={disabled}>
      <FormLabel component="legend" sx={{ fontSize: 12 }}>{label}</FormLabel>
      <RadioGroup row value={value} onChange={e => onChange(e.target.value as T)}>
        {options.map(o => (
          <FormControlLabel key={o.value} value={o.value} label={o.label}
            control={<Radio size="small" />} slotProps={{ typography: { fontSize: 12.5 } }} />
        ))}
      </RadioGroup>
    </FormControl>
  )
}

/** An on/off setting with its label. */
export function ToggleSwitch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <FormControlLabel control={<Switch checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />} label={label} />
}

/** A yes/no choice with its label (a default, an option). On/off state that takes effect uses ToggleSwitch. */
export function CheckboxField({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <FormControlLabel control={<Checkbox size="small" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />} label={label} slotProps={{ typography: { fontSize: 13 } }} />
}
